// Approver Portal — Standalone API Service

import { ensureFreshToken, silentRefresh } from './auth';

const API_BASE_URL = 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
const APPROVER_ENDPOINT = `${API_BASE_URL}/approver-api`;

export const VIEWER_URL = 'https://permissions-access-management.tse.amazon.dev/viewer';

const isLocal = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const RETURN_URL_KEY_LOCAL = 'approver_auth_return_url';

// Last-resort redirect (refresh token dead / no session). Preserves the current URL.
const handleUnauthorized = () => {
    sessionStorage.setItem(RETURN_URL_KEY_LOCAL, window.location.pathname + window.location.search);
    sessionStorage.removeItem('pam_approver_auth_session');
    window.location.href = '/';
};

// ════════════════════════════════════════════
// Centralized approver-api call: ensures a fresh (non-expired) token BEFORE the request,
// and on a 401 silently refreshes the token and retries ONCE. Only redirects to Federate
// as a last resort (refresh token dead). This fixes the "API error: 401" on comment posts
// and every other approver action when the ~15-min access token has expired.
// ════════════════════════════════════════════
const approverApiFetch = async (bodyObj) => {
    const doFetch = (token) => fetch(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && !isLocal() ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(bodyObj)
    });

    // Pre-flight: make sure we have a usable token (silently refreshes if near/at expiry).
    let token = isLocal() ? '' : await ensureFreshToken();
    let response = await doFetch(token);

    if (response.status === 401 && !isLocal()) {
        // Token was rejected — try one silent refresh + retry.
        const fresh = await silentRefresh();
        if (fresh) {
            response = await doFetch(fresh);
        }
        if (response.status === 401) {
            // Refresh unavailable/failed → redirect to re-auth as last resort.
            handleUnauthorized();
            throw new Error('Session expired. Redirecting to login...');
        }
    }
    return response;
};

// Generic fresh-token + 401-retry fetch for any endpoint on our API (validate, ai-assist).
const authedFetch = async (url, bodyObj) => {
    const doFetch = (token) => fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && !isLocal() ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(bodyObj)
    });
    let token = isLocal() ? '' : await ensureFreshToken();
    let response = await doFetch(token);
    if (response.status === 401 && !isLocal()) {
        const fresh = await silentRefresh();
        if (fresh) response = await doFetch(fresh);
        if (response.status === 401) {
            handleUnauthorized();
            throw new Error('Session expired. Redirecting to login...');
        }
    }
    return response;
};

// Clear messages for the server-side checks: 403 = this session may not use approver operations;
// 400 alias mismatch = actions are always taken as the signed-in user.
const apiErrorMessage = (status, parsed) => {
    const msg = (parsed && parsed.error) || '';
    if (status === 403) {
        return 'Not authorized for approver operations. Sign in to the approver portal again; access requires the permissionmanagement group.';
    }
    if (status === 400 && /must match the signed-in user/i.test(msg)) {
        return 'Approver alias must match the signed-in user. Refresh the page to act as yourself.';
    }
    return msg || `API error: ${status}`;
};

// Parse a standard approver-api response, throwing on error.
const parseApi = async (response) => {
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (!response.ok || (parsed && parsed.error)) {
        throw new Error(apiErrorMessage(response.status, parsed));
    }
    return parsed;
};

// Parse a response that may not carry a .error field (list/dashboard style).
const parseApiLoose = async (response) => {
    if (!response.ok) {
        const data = await response.json().catch(() => null);
        const parsed = data && typeof data.body === 'string' ? JSON.parse(data.body) : data;
        throw new Error(apiErrorMessage(response.status, parsed));
    }
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// ════════════════════════════════════════════
// Dashboard
// ════════════════════════════════════════════
export const getApproverDashboard = async () => {
    return parseApiLoose(await approverApiFetch({ operation: 'getDashboard' }));
};

// ════════════════════════════════════════════
// List Requests
// ════════════════════════════════════════════
export const listRequests = async (status) => {
    return parseApiLoose(await approverApiFetch({ operation: 'listRequests', status }));
};

// ════════════════════════════════════════════
// List Closed Requests
// ════════════════════════════════════════════
export const listClosedRequests = async (lastKey = null) => {
    return parseApiLoose(await approverApiFetch({ operation: 'listClosed', lastKey }));
};

// ════════════════════════════════════════════
// Update Request Status
// ════════════════════════════════════════════
export const updateRequestStatus = async (requestId, status, approver, notes) => {
    const response = await approverApiFetch({
        operation: 'updateStatus', requestId, newStatus: status, approver, notes
    });
    return parseApi(response);
};

// ════════════════════════════════════════════
// Get Request Details
// ════════════════════════════════════════════
export const getRequestDetails = async (requestId) => {
    return parseApiLoose(await authedFetch(`${API_BASE_URL}/validate`, { operation: 'getResults', requestId }));
};


// ════════════════════════════════════════════
// AI Approver Analysis
// ════════════════════════════════════════════
export const getApproverAIAnalysis = async (requestData) => {
    return parseApiLoose(await authedFetch(`${API_BASE_URL}/ai-assist`, { operation: 'approverAnalysis', context: requestData }));
};


// ════════════════════════════════════════════
// Dashboard Metrics
// ════════════════════════════════════════════
export const getDashboardMetrics = async () => {
    return parseApiLoose(await approverApiFetch({ operation: 'getMetrics' }));
};

// ════════════════════════════════════════════
// Requester History
// ════════════════════════════════════════════
export const getRequesterHistory = async (requesterAlias) => {
    return parseApiLoose(await approverApiFetch({ operation: 'requesterHistory', requesterAlias }));
};


// ════════════════════════════════════════════
// Get L8 Approval Status
// ════════════════════════════════════════════
export const getL8Status = async (requestId) => {
    return parseApi(await approverApiFetch({ operation: 'getL8Status', requestId }));
};

// ════════════════════════════════════════════
// Get Comments
// ════════════════════════════════════════════
export const getComments = async (requestId) => {
    return parseApi(await approverApiFetch({ operation: 'getComments', requestId }));
};

// ════════════════════════════════════════════
// Add Comment
// ════════════════════════════════════════════
export const addComment = async (requestId, alias, message) => {
    const response = await approverApiFetch({ operation: 'addComment', requestId, alias, message });
    return parseApi(response);
};


// ════════════════════════════════════════════
// Team Config — Admin CRUD
// ════════════════════════════════════════════
export const saveTeamConfig = async (configData) => {
    return parseApi(await approverApiFetch({ operation: 'saveTeamConfig', ...configData }));
};

export const getTeamConfig = async (teamLabel) => {
    return parseApi(await approverApiFetch({ operation: 'getTeamConfig', teamLabel }));
};

export const listTeamConfigs = async () => {
    return parseApi(await approverApiFetch({ operation: 'listTeamConfigs' }));
};

export const deleteTeamConfig = async (teamLabel) => {
    return parseApi(await approverApiFetch({ operation: 'deleteTeamConfig', teamLabel }));
};


// ════════════════════════════════════════════
// T321 Team Config — Admin CRUD
// ════════════════════════════════════════════
export const saveT321TeamConfig = async (configData) => {
    return parseApi(await approverApiFetch({ operation: 'saveT321TeamConfig', ...configData }));
};

export const getT321TeamConfig = async (teamLabel) => {
    return parseApi(await approverApiFetch({ operation: 'getT321TeamConfig', teamLabel }));
};

export const listT321TeamConfigs = async () => {
    return parseApi(await approverApiFetch({ operation: 'listT321TeamConfigs' }));
};

export const deleteT321TeamConfig = async (teamLabel) => {
    return parseApi(await approverApiFetch({ operation: 'deleteT321TeamConfig', teamLabel }));
};


// ════════════════════════════════════════════
// Hold Request
// ════════════════════════════════════════════
export const holdRequest = async (requestId, approver, notes = '') => {
    return parseApi(await approverApiFetch({ operation: 'holdRequest', requestId, approver, notes }));
};

// ════════════════════════════════════════════
// Resume Request
// ════════════════════════════════════════════
export const resumeRequest = async (requestId, approver, notes = '') => {
    return parseApi(await approverApiFetch({ operation: 'resumeRequest', requestId, approver, notes }));
};

// ════════════════════════════════════════════
// List On-Hold Requests
// ════════════════════════════════════════════
export const listOnHoldRequests = async () => {
    return parseApiLoose(await approverApiFetch({ operation: 'listRequests', status: 'ON_HOLD' }));
};

// ════════════════════════════════════════════
// Sync — Trigger background sync with approvals.amazon.com
// ════════════════════════════════════════════
const SYNC_ENDPOINT = `${API_BASE_URL}/sync`;

export const triggerSync = async () => {
    return parseApiLoose(await authedFetch(SYNC_ENDPOINT, { operation: 'sync' }));
};

export const getSyncStatus = async () => {
    return parseApiLoose(await approverApiFetch({ operation: 'getSyncStatus' }));
};
